import {combine} from 'effector';
import {createGate} from 'effector-react';

import {fetchOriginalPhoto, fetchProductRemarkingHistory, fetchProductsDetail} from 'api/products';
import {RestError} from 'core/errors';
import {createGridTableModel} from 'core/hooks/grid2.0';
import {rootDomain} from 'core/rootDomain';
import {
    ProductCommonRaw,
    ProductOperationsDictionary,
    ProductOperationsRawRow,
    ProductOperationsReasonOption,
    ProductOperationsSelectOption,
    ProductRemarkingHistory,
    ProductTracing,
} from 'types/products-detail';

const domain = rootDomain.createDomain('ProductsDetailDomain');
export const Gate = createGate<{markingCode: string; gtin: string | null}>('ProductsDetailGate');
export const OriginalPhotoGate = createGate<{link: string}>('ProductsOriginalPhotoGate');

export const {
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    stateReset,
} = createGridTableModel({
    domain,
    Gate,
});

export const fetchDataFx = domain.createEffect({
    name: 'fetchCommonDataFx',
    handler: fetchProductsDetail,
});

export const fetchOperationsDataFx = domain.createEffect({
    name: 'fetchOperationsDataFx',
    handler: fetchProductsDetail,
});

export const fetchOriginalPhotoFx = domain.createEffect({
    name: 'fetchOriginalPhotoFx',
    handler: fetchOriginalPhoto,
});

export const fetchRemarkingDataFx = domain.createEffect({
    name: 'fetchRemarkingDataFx',
    handler: fetchProductRemarkingHistory,
});

export const commonData$ = domain.createStore<ProductCommonRaw | null>(null);
export const operationsData$ = domain.createStore<ProductOperationsRawRow[]>([]);

const uniqueOptions = <T extends string | number>(
    operations: ProductOperationsRawRow[],
    getOption: (operation: ProductOperationsRawRow) => ProductOperationsSelectOption<T> | null,
) => {
    const options = new Map<T, ProductOperationsSelectOption<T>>();

    operations.forEach((operation) => {
        const option = getOption(operation);

        if (option?.label && option.value !== '' && option.value != null)
            options.set(option.value, option);
    });

    return Array.from(options.values());
};

const uniqueReasonOptions = (operations: ProductOperationsRawRow[]) => {
    const options = new Map<string, ProductOperationsReasonOption>();

    operations.forEach(({reason, operationAction}) => {
        if (reason) options.set(reason, {value: reason, operationAction});
    });

    return Array.from(options.values()).sort((a, b) => a.value.localeCompare(b.value));
};

const mapStorageLocationOption = (
    id: string | null,
    name: string | null,
): ProductOperationsSelectOption<number> | null => {
    const value = Number(id);

    return Number.isFinite(value) && name ? {label: name, value} : null;
};

export const operationsDictionary$ = operationsData$.map(
    (operations): ProductOperationsDictionary => ({
        reason: uniqueReasonOptions(operations),
        fromStorageLocation: uniqueOptions(
            operations,
            ({fromStorageLocationId, fromStorageLocationName}) =>
                mapStorageLocationOption(fromStorageLocationId, fromStorageLocationName),
        ),
        toStorageLocation: uniqueOptions(
            operations,
            ({toStorageLocationId, toStorageLocationName}) =>
                mapStorageLocationOption(toStorageLocationId, toStorageLocationName),
        ),
    }),
);

export const codeTracingData$ = domain.createStore<{
    operations: ProductOperationsRawRow[];
    tracing: ProductTracing[];
} | null>(null);

export const error$ = domain.createStore<RestError | null>(null);
export const childrenTotalCount$ = commonData$.map((data) => data?.childrenTotalCount || 0);

export const originalPhoto$ = domain.createStore<string | null>(null);
export const originalPhotoError$ = domain.createStore<boolean>(false);
export const remarkingData$ = domain.createStore<ProductRemarkingHistory | null>(null);
export const remarkingError$ = domain.createStore(false);

export const commonDataState$ = combine({
    data: commonData$,
    error: error$,
    isLoading: fetchDataFx.pending,
});

export const operationsTableState$ = combine({
    data: operationsData$,
    filters: filters$,
    dictionary: operationsDictionary$,
    error: error$,
    isLoading: fetchOperationsDataFx.pending,
});

export const codeTracingState$ = combine({
    data: codeTracingData$,
    error: error$,
    isLoading: fetchDataFx.pending,
});

export const originalPhotoState$ = combine({
    photo: originalPhoto$,
    error: originalPhotoError$,
    isLoading: fetchOriginalPhotoFx.pending,
});

export const remarkingDataState$ = combine({
    data: remarkingData$,
    hasError: remarkingError$,
    isLoading: fetchRemarkingDataFx.pending,
});
